/* ControlLearningPage — Etapa 9 */
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { controlApi, useOnlineStatus } from "../lib/controlApi";
import { ChevronLeft, Loader2, Sparkles, CheckCircle2, XCircle, TrendingUp, TrendingDown, ArrowRight } from "lucide-react";

const TYPE_LABEL = {
  publication_threshold_change: "Umbral de publicación",
  combined_priority_change: "Prioridad combinada",
  location_weight_change: "Peso por ubicación",
  category_weight_change: "Peso por categoría",
  facebook_weight_change: "Umbral Facebook",
  instagram_weight_change: "Umbral Instagram",
};

const CONF_LABEL = { high: "Alta", medium: "Media", low: "Baja" };

export default function ControlLearningPage() {
  const online = useOnlineStatus();
  const [data, setData] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [days, setDays] = useState(30);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([
        controlApi.learning(days),
        controlApi.suggestions(),
      ]);
      setData(l); setSuggestions((s.items || []));
    } catch { toast.error("No pudimos cargar aprendizaje"); }
    finally { setLoading(false); }
  }, [days]);
  useEffect(() => { load(); }, [load]);

  const doAccept = async (id) => {
    if (!online) { toast.error("Requiere conexión."); return; }
    setBusy(id);
    try {
      const r = await controlApi.acceptSuggestion(id);
      toast.success(`Aplicada: ${r.applied_change.before} → ${r.applied_change.after}`);
      load();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail?.error?.message || "Error");
    } finally { setBusy(""); }
  };
  const doReject = async (id) => {
    if (!online) { toast.error("Requiere conexión."); return; }
    setBusy(id);
    try {
      await controlApi.rejectSuggestion(id);
      toast.success("Ignorada.");
      load();
    } catch { toast.error("Error"); } finally { setBusy(""); }
  };

  if (loading) return <div className="p-6 text-textgray text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>;

  const m = (data && data.metrics) || {};
  const pending = suggestions.filter((s) => s.status === "pending");
  const reviewed = suggestions.filter((s) => s.status !== "pending").slice(0, 10);

  return (
    <div className="p-4 space-y-4" data-testid="spc-learning">
      <div className="flex items-center gap-2">
        <Link to="/control/more" className="text-textgray"><ChevronLeft className="w-4 h-4" /></Link>
        <div>
          <div className="text-[10px] uppercase tracking-[0.35em] text-textgray">Aprendizaje editorial</div>
          <div className="font-serif text-xl font-bold text-blueblack">Feedback supervisado</div>
        </div>
      </div>

      {/* Autonomy metric */}
      <div className="border border-softblue bg-white p-4 space-y-3" data-testid="spc-autonomy">
        <div className="flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-widest text-textgray font-bold">Autonomía editorial</div>
          <div className="flex gap-1">
            {[7, 30, 90].map((d) => (
              <button key={d} onClick={() => setDays(d)} data-testid={`spc-autonomy-${d}d`}
                className={`h-6 px-2 text-[10px] uppercase tracking-widest font-bold border ${days === d ? "bg-blueblack text-white border-blueblack" : "bg-white text-blueblack border-softblue"}`}>
                {d}d
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-baseline gap-2">
          <div className="font-serif text-4xl font-bold text-emerald-600">{m.autonomy_pct}%</div>
          <div className="text-xs text-textgray">automatización real</div>
        </div>
        <div className="grid grid-cols-3 gap-2 text-center text-xs pt-2 border-t border-softblue">
          <div>
            <div className="font-serif text-lg font-bold text-blueblack">{m.auto_published || 0}</div>
            <div className="text-[10px] uppercase tracking-widest text-textgray">Publicadas</div>
          </div>
          <div>
            <div className="font-serif text-lg font-bold text-blueblack">{m.editorial_interventions || 0}</div>
            <div className="text-[10px] uppercase tracking-widest text-textgray">Correcciones</div>
          </div>
          <div>
            <div className="font-serif text-lg font-bold text-blueblack">{m.retention_pct || 0}%</div>
            <div className="text-[10px] uppercase tracking-widest text-textgray">Retención</div>
          </div>
        </div>
      </div>

      {/* Pending suggestions */}
      <div className="border border-softblue bg-white">
        <div className="px-4 py-2 border-b border-softblue flex items-center gap-2 bg-lightgray/40">
          <Sparkles className="w-3.5 h-3.5 text-nightblue" />
          <div className="text-[10px] uppercase tracking-widest text-blueblack font-bold flex-1">Sugerencias pendientes</div>
          <span className="text-[10px] uppercase tracking-widest text-textgray font-bold">{pending.length}</span>
        </div>
        {pending.length === 0 ? (
          <div className="p-6 text-center text-textgray text-xs flex flex-col items-center gap-1">
            <CheckCircle2 className="w-5 h-5 text-emerald-500" />
            Sin sugerencias por ahora. El motor está en sintonía con tu criterio.
          </div>
        ) : (
          <ul className="divide-y divide-softblue">
            {pending.slice(0, 3).map((s) => (
              <SuggestionCard key={s.id} sug={s} busy={busy === s.id}
                onAccept={() => doAccept(s.id)} onReject={() => doReject(s.id)} />
            ))}
          </ul>
        )}
      </div>

      {reviewed.length > 0 && (
        <div className="border border-softblue bg-white">
          <div className="px-4 py-2 border-b border-softblue text-[10px] uppercase tracking-widest text-textgray font-bold">Historial reciente</div>
          <ul className="divide-y divide-softblue">
            {reviewed.map((s) => (
              <li key={s.id} className="p-3 text-xs flex items-start gap-2">
                {s.status === "accepted" ? <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /> : <XCircle className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-blueblack">{TYPE_LABEL[s.type] || s.type}</div>
                  <div className="text-textgray text-[11px]">
                    {s.scope?.category_slug || "—"}{s.scope?.location_id ? ` · ${s.scope.location_id}` : ""} · {s.status}
                    {s.applied_change && ` (${s.applied_change.before} → ${s.applied_change.after})`}
                  </div>
                  <div className="text-[10px] text-textgray">{s.reviewed_at && new Date(s.reviewed_at).toLocaleString("es-AR", { timeZone: "America/Argentina/Ushuaia" })}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="text-[10px] text-textgray text-center px-4">
        Las sugerencias se calculan cada día con datos de los últimos {days} días.
        Ningún cambio se aplica sin tu aprobación explícita.
      </div>
    </div>
  );
}

function SuggestionCard({ sug, busy, onAccept, onReject }) {
  const isUp = (sug.suggested_delta || 0) > 0;
  return (
    <li data-testid={`spc-suggestion-${sug.id}`} className="p-4 space-y-2">
      <div className="flex items-center gap-2">
        {isUp ? <TrendingUp className="w-4 h-4 text-emerald-600" /> : <TrendingDown className="w-4 h-4 text-amber-600" />}
        <div className="text-sm font-bold text-blueblack flex-1">{TYPE_LABEL[sug.type] || sug.type}</div>
        <span className="text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 border border-softblue text-textgray">
          Confianza {CONF_LABEL[sug.confidence] || sug.confidence}
        </span>
      </div>
      <div className="text-xs text-textgray">{sug.human_reason}</div>
      <div className="flex items-center gap-2 text-[11px]">
        <span className="text-textgray">Ajuste:</span>
        <span className="px-1.5 py-0.5 bg-slate-100 font-mono">{sug.current_value_ref}</span>
        <ArrowRight className="w-3 h-3 text-textgray" />
        <span className={`px-1.5 py-0.5 font-mono font-bold ${isUp ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
          {sug.suggested_delta > 0 ? "+" : ""}{sug.suggested_delta}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2 pt-1">
        <button onClick={onReject} disabled={busy} data-testid={`spc-suggestion-reject-${sug.id}`}
          className="h-11 border border-softblue bg-white text-blueblack text-[11px] uppercase tracking-widest font-bold disabled:opacity-50">
          Ignorar
        </button>
        <button onClick={onAccept} disabled={busy} data-testid={`spc-suggestion-accept-${sug.id}`}
          className="h-11 bg-blueblack text-white text-[11px] uppercase tracking-widest font-bold disabled:opacity-50 flex items-center justify-center">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Aceptar"}
        </button>
      </div>
    </li>
  );
}
